import {
  BaseMessage,
  MessageType,
  MessageHandler,
  UnsubscribeFunction,
  HandlerContext,
} from "./types";

export class PostMessageService {
  private static instance: PostMessageService | null = null;
  private readonly handlers: Map<MessageType, Set<MessageHandler>> = new Map();
  private context: HandlerContext;
  private isListening: boolean = false;

  private constructor(context: HandlerContext) {
    this.context = context;
  }

  public static getInstance(context?: HandlerContext): PostMessageService {
    if (!PostMessageService.instance) {
      if (!context) {
        throw new Error("PostMessageService requires context on first initialization");
      }
      PostMessageService.instance = new PostMessageService(context);
    }
    return PostMessageService.instance;
  }

  public static resetInstance(): void {
    if (PostMessageService.instance) {
      PostMessageService.instance.stopListening();
      PostMessageService.instance = null;
    }
  }

  public updateContext(context: Partial<HandlerContext>): void {
    this.context = { ...this.context, ...context };
    console.log("[PostMessageService] Context updated:", this.context);
  }

  public subscribe<T extends BaseMessage>(
    type: MessageType,
    handler: MessageHandler<T>
  ): UnsubscribeFunction {
    console.log(`[PostMessageService] Subscribing to ${type} messages`);

    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }

    this.handlers.get(type)!.add(handler as MessageHandler);

    if (!this.isListening) {
      this.startListening();
    }

    return () => {
      console.log(`[PostMessageService] Unsubscribing from ${type} messages`);
      const handlers = this.handlers.get(type);
      if (handlers) {
        handlers.delete(handler as MessageHandler);
        if (handlers.size === 0) {
          this.handlers.delete(type);
        }
      }

      if (this.handlers.size === 0) {
        this.stopListening();
      }
    };
  }

  public send<T extends BaseMessage>(
    message: T,
    targetWindow: Window = window.parent,
    targetOrigin: string = "*"
  ): void {
    console.log(`[PostMessageService] Sending ${message.type} message`, {
      message,
      targetOrigin,
    });

    if (targetWindow && targetWindow !== window) {
      targetWindow.postMessage(message, targetOrigin);
    } else {
      console.warn("[PostMessageService] Invalid target window for postMessage");
    }
  }

  private startListening(): void {
    if (this.isListening) {
      console.warn("[PostMessageService] Already listening");
      return;
    }

    console.log("[PostMessageService] Starting to listen for messages", {
      allowedOrigins: this.context.allowedOrigins,
      isDevelopment: this.context.isDevelopment,
    });

    window.addEventListener("message", this.handleMessage);
    this.isListening = true;
  }

  private stopListening(): void {
    if (!this.isListening) {
      return;
    }

    console.log("[PostMessageService] Stopping message listener");
    window.removeEventListener("message", this.handleMessage);
    this.isListening = false;
  }

  private readonly handleMessage = (event: globalThis.MessageEvent): void => {
    const messageType = event.data?.type as MessageType;

    console.log("[PostMessageService] Message received:", {
      type: messageType,
      origin: event.origin,
      isFromParent: event.source === window.parent,
      data: event.data,
    });

    if (!messageType) {
      console.log("[PostMessageService] Ignoring message without type");
      return;
    }

    if (!this.validateOrigin(event)) {
      console.warn("[PostMessageService] Message rejected due to invalid origin");
      return;
    }

    const handlers = this.handlers.get(messageType);
    if (!handlers || handlers.size === 0) {
      console.log(`[PostMessageService] No handlers for ${messageType} messages`);
      return;
    }

    console.log(`[PostMessageService] Dispatching to ${handlers.size} handler(s) for ${messageType}`);

    handlers.forEach((handler) => {
      try {
        const result = handler(event.data, {
          origin: event.origin,
          source: event.source as WindowProxy,
          data: event.data,
        });

        if (result instanceof Promise) {
          result.catch((error) => {
            console.error(`[PostMessageService] Handler error for ${messageType}:`, error);
          });
        }
      } catch (error) {
        console.error(`[PostMessageService] Handler error for ${messageType}:`, error);
      }
    });
  };

  private validateOrigin(event: globalThis.MessageEvent): boolean {
    const isFromParent = event.source === window.parent;
    const isNullOrigin = !event.origin || event.origin === "null";

    console.log("[PostMessageService] Validating origin:", {
      origin: event.origin,
      isFromParent,
      isNullOrigin,
      isDevelopment: this.context.isDevelopment,
      allowedOrigins: this.context.allowedOrigins,
    });

    if (isNullOrigin) {
      if (this.context.isDevelopment && isFromParent) {
        console.log("[PostMessageService] Allowing null origin in development (file:// protocol)");
        return true;
      }
      console.warn("[PostMessageService] Rejecting null origin (security risk)");
      return false;
    }

    const isAllowed = this.context.allowedOrigins.some((allowed) => {
      if (allowed === "*") return true;
      if (allowed === event.origin) return true;
      if (allowed.endsWith("*") && event.origin.startsWith(allowed.slice(0, -1))) return true;
      return false;
    });

    if (!isAllowed) {
      console.warn("[PostMessageService] Origin not in allowed list:", {
        received: event.origin,
        allowed: this.context.allowedOrigins,
      });
      return false;
    }

    return true;
  }

  public getHandlerCount(): number {
    let count = 0;
    this.handlers.forEach((handlers) => {
      count += handlers.size;
    });
    return count;
  }

  public isActive(): boolean {
    return this.isListening;
  }
}

export const createPostMessageService = (context: HandlerContext): PostMessageService => {
  return PostMessageService.getInstance(context);
};
